import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Alert, Typography, Spin, Space, Divider, message } from 'antd';
import { CopyOutlined, PrinterOutlined } from '@ant-design/icons';
import QRCode from 'react-qr-code';
import { propertyService, divisionService } from '@iso/services';

const { Paragraph } = Typography;

/**
 * Property Settings → Property QR Code tab.
 * Shows the single property landing QR (publish_code → /property-checkin).
 */
const PropertyQrPanel = ({ propertyId }) => {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(false);
  const qrRef = useRef(null);
  const whatsappQrRef = useRef(null);
  const [divisions, setDivisions] = useState([]);
  // One QR per division; keyed by division id so each can be printed on its own.
  const divisionQrRefs = useRef({});

  const load = useCallback(() => {
    if (!propertyId) {
      return;
    }
    setLoading(true);
    propertyService
      .viewProperty(propertyId)
      .then((res) => {
        if (res && res.property) {
          setProperty(res.property);
        }
      })
      .catch(() => message.error('Failed to load property QR'))
      .finally(() => setLoading(false));
  }, [propertyId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!propertyId) {
      return;
    }
    divisionService
      .getListForProperty(propertyId)
      .then((res) => {
        if (res && res.code === '0000') {
          // Group divisions not set up at this site (no site row) are not offered
          // on the landing, so a QR for one would do nothing.
          setDivisions((res.divisions || []).filter((d) => d.site));
        }
      })
      .catch(() => setDivisions([]));
  }, [propertyId]);

  const primaryId = property && property.primary_check_in_form_id;
  const contractorId = property && property.contractor_sign_in_form_id;
  const landingUrl = property && property.checkin_landing_url;
  // Null until the WhatsApp channel has a usable number, so the section simply
  // does not appear rather than printing a QR that goes nowhere.
  const whatsappUrl = property && property.whatsapp_checkin_url;
  // Same landing, with the division pre-selected for contractors.
  const divisionUrl = (divisionId) =>
    `${landingUrl}${landingUrl.indexOf('?') === -1 ? '?' : '&'}division=${encodeURIComponent(divisionId)}`;

  const copyLink = (url) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        message.success('Link copied');
      }).catch(() => message.error('Could not copy link'));
    } else {
      message.info('Copy the link shown above');
    }
  };

  const escapeHtml = (str) =>
    String(str || '').replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

  const handlePrint = (ref = qrRef, url = landingUrl, heading = 'Property QR Code') => {
    const svg = ref.current && ref.current.querySelector('svg');
    if (!svg) {
      message.error('QR code is not ready yet');
      return;
    }
    const printWindow = window.open('', '_blank', 'width=480,height=640');
    if (!printWindow) {
      message.error('Please allow pop-ups to print the QR code');
      return;
    }
    const name = escapeHtml(property && property.name ? property.name : 'Property');
    const safeUrl = escapeHtml(url);
    printWindow.document.write(
      '<html><head><title>' + escapeHtml(heading) + ' — ' + name + '</title><style>' +
      'body{font-family:Arial,Helvetica,sans-serif;text-align:center;padding:32px;color:#111;}' +
      'h2{margin:0 0 4px;font-size:20px;}' +
      '.kind{margin:0 0 20px;font-size:13px;color:#666;}' +
      'svg{width:260px;height:260px;}' +
      '.url{margin-top:20px;font-size:12px;color:#555;word-break:break-all;}' +
      '</style></head><body>' +
      '<h2>' + name + '</h2>' +
      '<div class="kind">' + escapeHtml(heading) + '</div>' +
      svg.outerHTML +
      '<div class="url">' + safeUrl + '</div>' +
      '<script>window.onload=function(){window.focus();window.print();window.onafterprint=function(){window.close();};};</script>' +
      '</body></html>'
    );
    printWindow.document.close();
  };

  if (loading && !property) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <p style={{ color: '#666', marginBottom: 16 }}>
        One QR code for guests. They open a welcome page and choose Visitor or Contractor, then fill the matching check-in form.
      </p>

      {!primaryId && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="No Primary check-in form set — the Visitor option will be unavailable on the landing page."
        />
      )}
      {!contractorId && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="No Contractor sign-in form set — the Contractor option will be unavailable on the landing page."
        />
      )}

      {landingUrl ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
          <div ref={qrRef} style={{ background: '#fff', padding: 12, border: '1px solid #f0f0f0', borderRadius: 4 }}>
            <QRCode value={landingUrl} size={160} />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Paragraph copyable={{ text: landingUrl }} style={{ marginBottom: 8, wordBreak: 'break-all' }}>
              {landingUrl}
            </Paragraph>
            <Space>
              <Button
                icon={<CopyOutlined />}
                onClick={() => {
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(landingUrl).then(() => {
                      message.success('Link copied');
                    }).catch(() => message.error('Could not copy link'));
                  } else {
                    message.info('Copy the link shown above');
                  }
                }}
              >
                Copy Link
              </Button>
              <Button icon={<PrinterOutlined />} onClick={() => handlePrint()}>
                Print QR Code
              </Button>
            </Space>
          </div>
        </div>
      ) : (
        <Alert
          type="info"
          showIcon
          message="Property QR code is unavailable because this property has no publish code."
        />
      )}

      {landingUrl && divisions.length > 0 && (
        <>
          <Divider />
          <h3 style={{ marginBottom: 4 }}>Division QR codes</h3>
          <p style={{ color: '#666', marginBottom: 16 }}>
            Put these up at each division's hub (e.g. the maintenance workshop). A contractor who
            scans one has that division already chosen, and can still change it.
          </p>
          {divisions.map((d) => {
            const url = divisionUrl(d.id);
            return (
              <div
                key={d.id}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start', marginBottom: 24 }}
              >
                <div
                  ref={(el) => { divisionQrRefs.current[d.id] = el; }}
                  style={{ background: '#fff', padding: 12, border: '1px solid #f0f0f0', borderRadius: 4 }}
                >
                  <QRCode value={url} size={160} title={`QR code for ${d.name}`} />
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <h4 style={{ marginBottom: 4 }}>{d.name}</h4>
                  <Paragraph copyable={{ text: url }} style={{ marginBottom: 8, wordBreak: 'break-all' }}>
                    {url}
                  </Paragraph>
                  <Space>
                    <Button icon={<CopyOutlined />} onClick={() => copyLink(url)}>
                      Copy Link
                    </Button>
                    <Button
                      icon={<PrinterOutlined />}
                      onClick={() => handlePrint({ current: divisionQrRefs.current[d.id] }, url, `Contractor check-in — ${d.name}`)}
                    >
                      Print QR Code
                    </Button>
                  </Space>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* The SAME publish_code as above, as a WhatsApp deep link — one printed
          code per property, two ways in. Hidden entirely when the channel has
          no usable number, rather than showing a QR that goes nowhere. */}
      {whatsappUrl && (
        <>
          <Divider />
          <h3 style={{ marginBottom: 4 }}>WhatsApp QR Code</h3>
          <p style={{ color: '#666', marginBottom: 16 }}>
            Guests scan this to check in over WhatsApp — no app to install and no
            browser needed. Useful where mobile data is poor but messaging still works.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
            <div ref={whatsappQrRef} style={{ background: '#fff', padding: 12, border: '1px solid #f0f0f0', borderRadius: 4 }}>
              <QRCode value={whatsappUrl} size={160} />
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Paragraph copyable={{ text: whatsappUrl }} style={{ marginBottom: 8, wordBreak: 'break-all' }}>
                {whatsappUrl}
              </Paragraph>
              <Space>
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      navigator.clipboard.writeText(whatsappUrl).then(() => {
                        message.success('Link copied');
                      }).catch(() => message.error('Could not copy link'));
                    } else {
                      message.info('Copy the link shown above');
                    }
                  }}
                >
                  Copy Link
                </Button>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => handlePrint(whatsappQrRef, whatsappUrl, 'WhatsApp Check-in')}
                >
                  Print QR Code
                </Button>
              </Space>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PropertyQrPanel;
